import { ROUTES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { type Route } from "next"
import { usePathname, useRouter } from "next/navigation"
import * as R from "ramda"

export function usePrevious() {
  const [state, send] = useActor(newSubscriptionFlowService)
  const router = useRouter()
  const pathname = usePathname()
  const lastPet = R.last(state.context.quote.pets || [])

  const goToPreviousPage = () => {
    if (pathname.includes(ROUTES.petName.path)) {
      if (state.context.isFromLandingPage) {
        send({
          type: "SET_CURRENT_PET_IDX",
          petId: lastPet?.id ?? "",
          petIndex: lastPet?.idx ?? 0,
        })
        return router.push(ROUTES.prepackages.path)
      }
      if (R.any(R.has("created_at"))(state.context.quote?.pets || [])) {
        return router.push(state.context.termsAccepted ? ROUTES.tribeQuote.path : ROUTES.tribe.path)
      }
    }
    if (pathname.includes(ROUTES.petInfo.path)) {
      return router.push(ROUTES.petName.path)
    }
    if (pathname.includes(ROUTES.petBreed.path)) {
      return router.push(ROUTES.petInfo.path)
    }
    if (pathname.includes(ROUTES.petBirthdate.path)) {
      return router.push(ROUTES.petBreed.path)
    }
    if (pathname.includes(ROUTES.tribe.path) && !pathname.includes(ROUTES.tribeQuote.path)) {
      send({
        type: "SET_CURRENT_PET_IDX",
        petId: lastPet?.id ?? "",
        petIndex: lastPet?.idx ?? 0,
      })
      return router.push(ROUTES.petBirthdate.path)
    }
    if (pathname.includes(ROUTES.attributionQuestion.path)) {
      return router.push(ROUTES.tribe.path)
    }

    if (pathname.includes(ROUTES.medicalHistory.path)) {
      // Back from a pet's health-history → the step just before it for the SAME
      // pet: a dog's RC overview, or a cat's prepackages. currentPetIdx unchanged.
      const currentIdx = state.context.currentPetIdx
      const currentPet = (state.context.quote?.pets || []).find((p) => p.idx === currentIdx)
      if (currentPet?.id && typeof currentPet.idx === "number") {
        send({
          type: "SET_CURRENT_PET_IDX",
          petId: currentPet.id,
          petIndex: currentPet.idx,
        })
      }
      return router.push(
        currentPet?.type === "dog" ? (ROUTES.liabilityAddOn.path as Route) : ROUTES.prepackages.path
      )
    }
    if (pathname.includes(ROUTES.liabilityAddOn.path)) {
      // Back from a dog's RC overview → that same dog's prepackages.
      const currentIdx = state.context.currentPetIdx
      const currentDog = (state.context.quote?.pets || []).find((p) => p.idx === currentIdx)
      if (currentDog?.id && typeof currentDog.idx === "number") {
        send({
          type: "SET_CURRENT_PET_IDX",
          petId: currentDog.id,
          petIndex: currentDog.idx,
        })
      }
      return router.push(ROUTES.prepackages.path)
    }
    if (pathname.includes(ROUTES.tribeQuote.path)) {
      // Back to the last pet in the walk: its health-history is the final per-pet
      // step (for dogs and cats alike) before the tribe-quote hub.
      const lastByIdx = [...(state.context.quote?.pets || [])]
        .filter(
          (pet): pet is typeof pet & { idx: number; id: string } =>
            typeof pet.idx === "number" && Boolean(pet.id)
        )
        .sort((a, b) => b.idx - a.idx)[0]
      if (lastByIdx) {
        send({
          type: "SET_CURRENT_PET_IDX",
          petId: lastByIdx.id,
          petIndex: lastByIdx.idx,
        })
        return router.push(ROUTES.medicalHistory.path as Route)
      }
    }
    if (pathname.includes(ROUTES.healthCoverage.path)) {
      return router.push(ROUTES.prepackages.path)
    }
    if (pathname.includes(ROUTES.healthCoverageParam.path)) {
      return router.push(ROUTES.prepackages.path)
    }
    if (pathname.includes(ROUTES.prepackages.path)) {
      const currentIdx = state.context.currentPetIdx
      if (typeof currentIdx === "number") {
        const previousPet = (state.context.quote?.pets || [])
          .filter(
            (pet): pet is typeof pet & { idx: number; id: string } =>
              typeof pet.idx === "number" &&
              pet.idx < currentIdx &&
              Boolean(pet.id) &&
              Boolean(pet.pricing?.options?.pack)
          )
          .sort((a, b) => b.idx - a.idx)[0]
        if (previousPet) {
          send({
            type: "SET_CURRENT_PET_IDX",
            petId: previousPet.id,
            petIndex: previousPet.idx,
          })
          // The step before this pet's prepackages is the previous pet's
          // health-history (the last per-pet step, for dogs and cats alike).
          return router.push(ROUTES.medicalHistory.path as Route)
        }
      }
      return router.push(ROUTES.attributionQuestion.path)
    }
    if (pathname.includes(ROUTES.pricing.path)) {
      return router.push(ROUTES.healthCoverageParam.path)
    }
    if (pathname.includes(ROUTES.userInformations.path)) {
      send({
        type: "SET_CURRENT_PET_IDX",
        petId: lastPet?.id ?? "",
        petIndex: lastPet?.idx ?? 0,
      })
      return router.push(ROUTES.tribeQuote.path)
    }
    if (pathname.includes(ROUTES.userBirthInformations.path)) {
      return router.push(ROUTES.userInformations.path)
    }
    if (pathname.includes(ROUTES.payment.path)) {
      return router.push(ROUTES.recap.path)
    }
    if (pathname.includes(ROUTES.recap.path)) {
      return router.push(ROUTES.userBirthInformations.path)
    }
  }

  return goToPreviousPage
}
